/**
 * 组织架构模块 - 常量定义
 * 
 * @description
 * 定义车间、生产线、工位的枚举标签映射和其他常量
 * 主要用于前端显示枚举值的中文标签
 * 
 * @module types/organization/constants
 * 
 * @对应后端文件
 * - Workshop Model: backend/app/Modules/Organization/Models/Workshop.php
 * - ProductionLine Model: backend/app/Modules/Organization/Models/ProductionLine.php
 * - Workstation Model: backend/app/Modules/Organization/Models/Workstation.php
 * 
 * @注意事项
 * 1. 常量名使用 UPPER_SNAKE_CASE
 * 2. 标签对象的 key 必须与枚举值完全一致
 * 3. 标签内容参考后端 Model 的 get*NameAttribute() 方法
 * 
 * @更新时机
 * - 新增枚举类型时
 * - 修改枚举标签时
 * - 新增业务常量时
 * 
 * @验证方式
 * 1. 对照 enums.ts 确保所有枚举都有对应的标签映射
 * 2. 对照 Model 的 get*NameAttribute() 方法确认标签内容
 * 3. 确保标签映射完整（无遗漏、无多余）
 * 
 * @last-updated 2025-10-19
 * @author AI Assistant
 */

import {
  WorkshopType,
  WorkshopStatus,
  ProductionLineType,
  ProductionLineStatus,
  WorkstationType,
  WorkstationStatus,
  QualityCheckType,
} from './enums';

/**
 * 车间类型标签映射
 * @see Workshop::getTypeNameAttribute()
 */
export const WORKSHOP_TYPE_LABELS: Record<WorkshopType, string> = {
  [WorkshopType.CNC]: '数控车间',
  [WorkshopType.HEAT_TREATMENT]: '热处理车间',
  [WorkshopType.SURFACE_TREATMENT]: '表面处理车间',
  [WorkshopType.ASSEMBLY]: '总装车间',
  [WorkshopType.INSPECTION]: '检测车间',
  [WorkshopType.WAREHOUSE]: '仓储车间',
  [WorkshopType.DEDICATED]: '专线车间',
};

/**
 * 车间状态标签映射
 * @see Workshop::getStatusNameAttribute()
 */
export const WORKSHOP_STATUS_LABELS: Record<WorkshopStatus, string> = {
  [WorkshopStatus.INACTIVE]: '停用',
  [WorkshopStatus.ACTIVE]: '启用',
};

/**
 * 生产线类型标签映射（汽车配件CNC加工行业）
 * @see ProductionLine::getTypeNameAttribute()
 */
export const PRODUCTION_LINE_TYPE_LABELS: Record<ProductionLineType, string> = {
  [ProductionLineType.CNC]: '数控加工线',
  [ProductionLineType.CUTTING]: '切割线',
  [ProductionLineType.CLEANING]: '清洗线',
  [ProductionLineType.HEAT_TREATMENT]: '热处理线',
  [ProductionLineType.SURFACE_TREATMENT]: '表面处理线',
  [ProductionLineType.INSPECTION]: '检测线',
  [ProductionLineType.ASSEMBLY]: '装配线',
  [ProductionLineType.PACKAGING]: '包装线',
};

/**
 * 生产线状态标签映射
 * @see ProductionLine::getStatusNameAttribute()
 */
export const PRODUCTION_LINE_STATUS_LABELS: Record<ProductionLineStatus, string> = {
  [ProductionLineStatus.INACTIVE]: '停用',
  [ProductionLineStatus.ACTIVE]: '启用',
  [ProductionLineStatus.MAINTENANCE]: '维护中',
};

/**
 * 工位类型标签映射
 * @see Workstation::getTypeNameAttribute()
 */
export const WORKSTATION_TYPE_LABELS: Record<WorkstationType, string> = {
  [WorkstationType.MANUAL]: '人工',
  [WorkstationType.SEMI_AUTO]: '半自动',
  [WorkstationType.AUTO]: '全自动',
};

/**
 * 工位状态标签映射
 * @see Workstation::getStatusNameAttribute()
 */
export const WORKSTATION_STATUS_LABELS: Record<WorkstationStatus, string> = {
  [WorkstationStatus.INACTIVE]: '停用',
  [WorkstationStatus.IDLE]: '空闲',
  [WorkstationStatus.WORKING]: '工作中',
  [WorkstationStatus.FAULT]: '故障',
  [WorkstationStatus.MAINTENANCE]: '维护中',
};

/**
 * 质检类型标签映射
 * @see Workstation::getQualityCheckTypeNameAttribute()
 */
export const QUALITY_CHECK_TYPE_LABELS: Record<QualityCheckType, string> = {
  [QualityCheckType.FIRST]: '首检',
  [QualityCheckType.PATROL]: '巡检',
  [QualityCheckType.FINAL]: '终检',
};

/**
 * 车间类型选项（用于下拉选择）
 */
export const WORKSHOP_TYPE_OPTIONS = Object.entries(WORKSHOP_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 车间状态选项（用于下拉选择）
 */
export const WORKSHOP_STATUS_OPTIONS = Object.entries(WORKSHOP_STATUS_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

/**
 * 生产线类型选项（用于下拉选择）
 */
export const PRODUCTION_LINE_TYPE_OPTIONS = Object.entries(PRODUCTION_LINE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 生产线状态选项（用于下拉选择）
 */
export const PRODUCTION_LINE_STATUS_OPTIONS = Object.entries(PRODUCTION_LINE_STATUS_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

/**
 * 工位类型选项（用于下拉选择）
 */
export const WORKSTATION_TYPE_OPTIONS = Object.entries(WORKSTATION_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 工位状态选项（用于下拉选择）
 */
export const WORKSTATION_STATUS_OPTIONS = Object.entries(WORKSTATION_STATUS_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

/**
 * 质检类型选项（用于下拉选择）
 */
export const QUALITY_CHECK_TYPE_OPTIONS = Object.entries(QUALITY_CHECK_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * ⚠️ DEFAULT_PAGE_SIZE 已移至 constants/default-config.ts
 * 
 * 请使用：
 * ```typescript
 * import { DEFAULT_PAGE_SIZE } from '@bayes-mes/shared/constants/default-config'
 * ```
 * 
 * @deprecated 请从 @bayes-mes/shared/constants/default-config 导入
 */
// export const DEFAULT_PAGE_SIZE = 20; - 已移至 constants/default-config.ts

/**
 * 默认排序字段
 */
export const DEFAULT_SORT = {
  WORKSHOP: 'order_num',
  PRODUCTION_LINE: 'order_num',
  WORKSTATION: 'sequence',
} as const;

/**
 * 默认工作时长（小时）
 */
export const DEFAULT_WORKING_HOURS = 8;

/**
 * 车间编码前缀
 */
export const WORKSHOP_CODE_PREFIX = 'WS';

/**
 * 生产线编码前缀
 */
export const PRODUCTION_LINE_CODE_PREFIX = 'LINE';

/**
 * 工位编码前缀
 */
export const WORKSTATION_CODE_PREFIX = 'ST';

